import { TileLayer } from '@deck.gl/geo-layers/typed';
import { useAppSelector } from 'hooks/redux';
import React, { useEffect, useRef, useState } from 'react';
import {
  MapContext,
  MapRef,
  NavigationControl,
  StaticMap,
  ViewState
} from 'react-map-gl';
import DeckGL from '@deck.gl/react/typed';
import { selectGeofences } from 'reducers/geofencesReducer';
import { BitmapLayer, GeoJsonLayer } from '@deck.gl/layers/typed';
import { getGeofences } from 'actions/geofenceActions';
import { getCells } from 'actions/orderActions';
import { useDispatch } from 'react-redux';
import { Address } from 'views/Restaurant/SelectedCellMap';
import { FlightCard } from 'models/flight-card.interface';
import { Cell } from '@m4a3/api-client';
import { getRoute } from 'actions/routePlannerActions';
import { cellNameCoords } from 'constants/index';
import { logToDatadog } from 'services/datadog';
import { ReactComponent as LayerIcon } from 'assets/svgs/Map.svg';
import booleanIntersects from '@turf/boolean-intersects'
import buffer from '@turf/buffer'
import { getDistanceBetweenPoints } from 'utils/parsingUtils';

interface FlightCardMapProps {
  selectedAddress: Address;
  selectedAircraft: string;
  selectedCell: Cell;
  filteredFlightCards: FlightCard[];
  allAdresses: Address[];
  componentHeight: number;
  componentWidth: number;
  onConflictChange: (isConflicting: boolean) => void;
  onBlockConflictNameChange: (isBlockConflictName: boolean) => void;
  onRouteLengthChange: (length: number) => void;
  onRouteTooLowChange: (isTooLow: boolean) => void;
  onRouteSlopedChange: (isSloped: boolean) => void;
  maxRecommendedDistance: number;
  maxRecommendedAltitude: number;
}

const FlightCardMapComponent: React.FC<FlightCardMapProps> = ({
  selectedAddress,
  selectedAircraft,
  selectedCell,
  filteredFlightCards,
  allAdresses,
  onConflictChange,
  onBlockConflictNameChange,
  onRouteLengthChange,
  onRouteTooLowChange,
  onRouteSlopedChange,
  maxRecommendedAltitude,
  maxRecommendedDistance
}) => {
  const mapRef = useRef<MapRef | null>(null);
  const [viewState, setViewState] = useState<ViewState>({
    latitude: selectedCell ? selectedCell.centerLatitude : cellNameCoords['BELAIR'][0],
    longitude: selectedCell ? selectedCell.centerLongitude : cellNameCoords['BELAIR'][1],
    zoom: 11,
    bearing: 0,
    pitch: 0
  });
  const [mapLayers, setMapLayers] = useState([]);
  const [mapStyle, setMapStyle] = useState('mapbox://styles/mapbox/satellite-v9');
  const [activeRoutesGeojsonData, setActiveRoutesGeojsonData] = useState({});
  const selectedRouteLineColor = [0, 200, 255];
  const conflictLineColor = [255, 0, 0];
  const amberLineColor = [255, 190, 0];

  const hoverRef = useRef(null);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipContent, setTooltipContent] = useState("");
  const [tooltipDimensions, setTooltipDimensions] = useState({ width: 0, height: 0 });

  const geofences = useAppSelector(selectGeofences);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getCells());
    dispatch(getGeofences());
    renderActiveRoutesOnMap();
  }, []);

  // update map view to new selected cell
  useEffect(() => {
    if (selectedCell !== null) {
      setViewState({
        latitude: selectedCell.centerLatitude,
        longitude: selectedCell.centerLongitude,
        zoom: 11,
        bearing: 0,
        pitch: 0
      });
    }
  }, [selectedCell])

  useEffect(() => {
    updateMapLayers();
  }, [filteredFlightCards, selectedAddress])

  useEffect(() => {
    if (hoverRef.current) {
      setTooltipDimensions({
        width: hoverRef.current.offsetWidth,
        height: hoverRef.current.offsetHeight
      });
    }
  }, [isTooltipVisible]);

  const updateMapLayers = async () => {
    try {
      let combinedLayers = [];
      if (selectedAddress) {
        const { layers, geojsonData } = await renderSelectedAddressOnMap();
        combinedLayers.push(...layers);
      }
      if (filteredFlightCards !== undefined && filteredFlightCards.length > 0) {
        const { layers, geojsonData } = await renderActiveRoutesOnMap();
        combinedLayers.push(...layers);
      }
      setMapLayers(combinedLayers);
    } catch (err) {
      console.error('Error updating map layers:', err);
      logToDatadog('error_updating_map_layers', { err });
    }
  };

  const checkForConflicts = (selectedRouteGeojson) => {
    const isConflicting = Object.entries(activeRoutesGeojsonData).some(([routeName, activeRoute]) => {
      const matchingFlightCard = filteredFlightCards.find(flightCard => flightCard.route === routeName);
      if (matchingFlightCard?.active) {
          const bufferedActiveRoute = buffer(activeRoute as any, 1, { units: 'meters' });
          const bufferedSelectedRoute = buffer(selectedRouteGeojson, 1, { units: 'meters' });
          return booleanIntersects(bufferedActiveRoute, bufferedSelectedRoute);
      }
      return false;
    });

    const isBlockConflictName = selectedAddress.eircode.includes('XWX');
    const isConflictingWithXWX = Object.entries(activeRoutesGeojsonData).some(([routeName, activeRoute]) => {
      if (routeName.toUpperCase().includes('XWX')) {
        const matchingFlightCard = filteredFlightCards.find(flightCard => flightCard.route === routeName);
        if (matchingFlightCard?.active) {
          const bufferedActiveRoute = buffer(activeRoute as any, 1, { units: 'meters' });
          const bufferedSelectedRoute = buffer(selectedRouteGeojson, 1, { units: 'meters' });
          return booleanIntersects(bufferedActiveRoute, bufferedSelectedRoute);
        }
      }
      return false;
    });

    onBlockConflictNameChange(isBlockConflictName || isConflictingWithXWX);
    onConflictChange(isConflicting);
    return isConflicting;
  }

  const checkRouteDistanceAndAltitude = (geojsonData) => {
    let totalDistance = 0;
    let routeValid = true;
    let lastCoords = null;
    let relativeAltitude = null;
    for (const feature of geojsonData.features) {
      const coords = feature.geometry.coordinates;
      if (!lastCoords) {
        lastCoords = coords;
        relativeAltitude = feature.properties.relativeAltitude;
        continue;
      }
      if (parseInt(feature.properties.relativeAltitude) < maxRecommendedAltitude) {
        onRouteTooLowChange(true);
        routeValid = false;
      }
      if (relativeAltitude !== feature.properties.relativeAltitude) {
        onRouteSlopedChange(true);
        routeValid = false;
      }
      const distanceBetweenPoints = getDistanceBetweenPoints(lastCoords[1], lastCoords[0], coords[1], coords[0]);
      totalDistance += distanceBetweenPoints;
      lastCoords = coords;
    }
    onRouteLengthChange(totalDistance/1000);
    if ((totalDistance / 1000) > maxRecommendedDistance) {
      routeValid = false;
    }
    return routeValid;
  };

  const getGeojsonLayers = (id, routeName, geojsonData, lineColor) => {
    return new Promise((resolve, reject) => {
      getRoute(id, res => {
        const pointGeojson = res.misc1;
        const lineString = res.misc2;
        if (lineColor === selectedRouteLineColor) {
          if (Object.keys(activeRoutesGeojsonData).length > 0 && checkForConflicts(lineString)) {
            lineColor = amberLineColor;
          } else if (!checkRouteDistanceAndAltitude(pointGeojson)) {
            lineColor = amberLineColor;
          }
        }
        resolve(new GeoJsonLayer({
          id: `route-${id}`,
          data: lineString,
          getLineColor: lineColor,
          lineWidthScale: 1,
          lineWidthMinPixels: 2,
          pickable: true,
          onHover: info => {
            if (info && info.object) {
              const relevantFlightCard = filteredFlightCards.find(flightCard => flightCard.route === routeName)
              const aircraft = (relevantFlightCard?.aircraft ?? selectedAircraft) || "No Aircraft Selected";
              setTooltipContent(`Route: ${routeName}\nAircraft: ${aircraft}\n`);
              setTooltipPosition({ x: info.x, y: info.y });
              setIsTooltipVisible(true);
            } else {
              setIsTooltipVisible(false);
            }
          }
        }));
        geojsonData[routeName] = lineString;
      }, err => {
        reject(err);
      });
    });
  }

  const addGeojsonDataToMap = async (promise, geojsonData) => {
    try {
      const layers = await Promise.all([promise]);
      return { layers, geojsonData };
    } catch (err) {
      logToDatadog('error_retrieving_geojson', { err });
      console.error('Error fetching route:', err);
      return { layers: [], geojsonData: {} };
    }
  };

  const renderActiveRoutesOnMap = async () => {
    let geojsonData = {};
    const routePromises = filteredFlightCards
      .filter(flightCard => flightCard.active)
      .map(flightCard => {
        const adressForFlightCard = allAdresses.find(address => address.orderId === flightCard.orderId);
        return getGeojsonLayers(adressForFlightCard.id, adressForFlightCard.eircode, geojsonData, [0, 255, 0]);
      });
    const { layers, geojsonData: activeRoutesData } = await addGeojsonDataToMap(Promise.all(routePromises), geojsonData);
    setActiveRoutesGeojsonData(activeRoutesData);
    return { layers, geojsonData: activeRoutesData };
  };

  const renderSelectedAddressOnMap = async () => {
    const geojsonData = {};
    const layerPromise = getGeojsonLayers(selectedAddress.id, selectedAddress.eircode, geojsonData, selectedRouteLineColor);
    return addGeojsonDataToMap(layerPromise, geojsonData);
  };

  const toggleMapStyle = () => {
    const isSatellite = mapStyle === 'mapbox://styles/mapbox/satellite-v9';
    setMapStyle(isSatellite ? 'mapbox://styles/mapbox/dark-v10' : 'mapbox://styles/mapbox/satellite-v9');
  };

  const geoJsonLayers = geofences.map(fence => new GeoJsonLayer({
    id: 'path-layer-fences' + fence.id,
    data: fence.geoJson,
    getWidth: 2,
    widthUnits: 'pixels',
    pickable: true,
    lineWidthScale: 0.5,
    lineWidthMinPixels: 0.5,
    getLineColor: [255, 255, 255],
    filled: false,
    getPointRadius: 0.5,
    getLineWidth: 8,
    opacity: 0.3
  }));

  const tileLayer = new TileLayer({
    id: 'satellite-tiles',
    data: 'https://tiles.manna.aero/{z}/{x}/{y}.png',
    minZoom: 12,
    maxZoom: 22,
    tileSize: 256,
    renderSubLayers: (props: any) => {
      const {
        bbox: { west, south, east, north }
      } = props.tile;
      return new BitmapLayer(props, {
        // @ts-ignore
        data: null,
        image: props.data,
        bounds: [west, south, east, north],
      });
    }
  });

  const layers = (mapStyle === 'mapbox://styles/mapbox/dark-v10')
    ? [...geoJsonLayers, ...mapLayers]
    : [tileLayer, ...geoJsonLayers, ...mapLayers];

  return (
    <div id="flightCardsMap" style={{ position: 'relative', width: '100%', height: '100%' }}>
      <DeckGL
        initialViewState={viewState}
        controller={{
          touchRotate: false,
          dragRotate: false,
          keyboard: false,
        }}
        // @ts-ignore
        ContextProvider={MapContext.Provider}
        layers={layers}
      >
        <StaticMap
          ref={mapRef}
          mapStyle={mapStyle}
          mapboxApiAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
        >
        </StaticMap>
        <NavigationControl
          style={{ position: 'absolute', top: 60, right: 10 }}
        />
      </DeckGL>
      <button onClick={toggleMapStyle} title="Toggle Map Style" style={{ position: 'absolute', top: 10, right: 5, padding: '5px', border: 'none', cursor: 'pointer', background: 'none' }}>
        <div style={{ backgroundColor: 'white', display: 'inline-block', padding: '5px', borderRadius: '20%', boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.2)' }}>
          <LayerIcon width={'20px'} height={'20px'} />
        </div>
      </button>
      <div>
        <span
          ref={hoverRef}
          style={{
            position: 'absolute',
            minHeight: '30px',
            minWidth: '50px',
            background: 'white',
            padding: '5px',
            zIndex: 1000,
            pointerEvents: 'none',
            opacity: isTooltipVisible ? 1 : 0,
            visibility: isTooltipVisible ? 'visible' : 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            whiteSpace: 'pre-line',
            borderRadius: '5px',
            left: `${tooltipPosition.x - tooltipDimensions.width / 2}px`,
            top: `${tooltipPosition.y - tooltipDimensions.height}px`,
            transition: isTooltipVisible ? 'none' : 'opacity 1s ease-out, visibility 1s ease-out',
          }}
        >
          {tooltipContent}
        </span>
      </div>
    </div>
  );
};

export default FlightCardMapComponent;
